import {
    CategoryMetadata,
    MeasureItemLeftPosition,
    MeasureSubcategoriesContainerWidth,
    SelectedCategory,
    WrapContainerRefs,
} from '@/features/pos/components/ProductCatalog/parts/CategoryBar/types'
import { useCallback, useEffect, useRef } from 'react'

type SelectedCategoryWithMetadata = SelectedCategory & CategoryMetadata

interface PrevAndPressedSelectedCategory {
    prev?: SelectedCategoryWithMetadata
    pressed: SelectedCategoryWithMetadata
}

interface ScrollMeasureHandlers {
    measureItemLeftPosition: MeasureItemLeftPosition
    lastSelectedMeasureSubcategoriesContainerWidth?: MeasureSubcategoriesContainerWidth
}

export const useScrollToCategory = (
    { containerRef, scrollWrapperRef }: WrapContainerRefs,
    disabled?: boolean
) => {
    const requestIdRef = useRef(0)
    const cancelPendingScroll = useCallback(() => {
        requestIdRef.current += 1
    }, [])

    useEffect(() => cancelPendingScroll, [cancelPendingScroll, disabled])

    const scrollToCategory = useCallback(
        async (
            { prev, pressed }: PrevAndPressedSelectedCategory,
            {
                measureItemLeftPosition,
                lastSelectedMeasureSubcategoriesContainerWidth,
            }: ScrollMeasureHandlers
        ) => {
            const requestId = ++requestIdRef.current

            if (disabled) return

            const isUnselect =
                prev?.id === pressed.id && prev?.subcategoryId === pressed.subcategoryId

            if (isUnselect) return

            const isSubcategoriesClosed = prev?.id !== pressed.id && !!prev?.hasSubcategories
            const isPressedAfterPrev = !!prev && pressed.index > prev.index

            try {
                const [itemLeft, subcategoriesContainerWidth] = await Promise.all([
                    measureItemLeftPosition(containerRef),
                    isSubcategoriesClosed && isPressedAfterPrev
                        ? (lastSelectedMeasureSubcategoriesContainerWidth?.() ?? 0)
                        : 0,
                ])

                if (
                    requestId !== requestIdRef.current ||
                    !Number.isFinite(itemLeft) ||
                    !Number.isFinite(subcategoriesContainerWidth)
                ) {
                    return
                }

                scrollWrapperRef.current?.scrollTo({
                    x: Math.max(0, itemLeft - subcategoriesContainerWidth),
                    animated: !isSubcategoriesClosed,
                })
            } catch {
                // Keeping the pressed category visible is optional; selection must still work.
            }
        },
        [containerRef, scrollWrapperRef, disabled]
    )

    return { scrollToCategory, cancelPendingScroll }
}
